// src/components/AccountSideBar/AuthModal.tsx
import React, { useEffect, useRef, useState } from "react";
import "./AuthModal.css";
import { useAuth } from "../../contexts/AuthContext";
import { signInWithApple, auth } from "../../lib/firebase";
import { Profile } from "../Profile/ProfileContext";
import { userService } from "../../services/userService";
import { trackCompleteRegistration } from "../../lib/analytics";
import { getErrorMessage } from "../../types/common";
import AuthLandingView from "./AuthLandingView";
import LoginView from "./LoginView";
import SignupView, { SignupForm } from "./SignupView";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate?: (profile: Profile) => void;
};

type View = "landing" | "login" | "email";

const emptyForm: SignupForm = {
  email: "",
  straat: "",
  voornaam: "",
  huisnummer: "",
  toevoeging: "",
  naam: "",
  postcode: "",
  wachtwoord: "",
  stad: "",
  land: "",
  iban: "",
  bic: "",
};

export default function AuthModal({ open, onClose }: Props) {
  const { login, googleLoginWithIdToken, refreshAuth } = useAuth();

  const [view, setView] = useState<View>("landing");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [form, setForm] = useState<SignupForm>(emptyForm);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // voorkomt dubbele popup calls
  const googleLock = useRef(false);
  const appleLock = useRef(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setView("landing");
    }
  }, [open]);

  if (!open) return null;

  const closeAll = () => {
    setError(null);
    setBusy(false);

    setUsername("");
    setPassword("");

    setForm(emptyForm);
    setAvatarUrl(null);

    setView("landing");
    onClose();
  };

  const openLogin = () => {
    setError(null);
    setView("login");
    setTimeout(() => firstInputRef.current?.focus(), 0);
  };

  const openSignupEmail = () => {
    setError(null);
    setView("email");
    setTimeout(() => firstInputRef.current?.focus(), 0);
  };

  // ---------------- LOGIN ----------------
  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(username, password);
      await refreshAuth();
      closeAll();
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Inloggen mislukt"));
    } finally {
      setBusy(false);
    }
  };

  // ---------------- GOOGLE ----------------
  const handleGoogleSuccess = async (credential: string) => {
    if (busy || googleLock.current) return;
    googleLock.current = true;

    setError(null);
    setBusy(true);
    try {
      await googleLoginWithIdToken(credential);

      await refreshAuth();
      closeAll();
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Google login mislukt"));
    } finally {
      setBusy(false);
      googleLock.current = false;
    }
  };

  // ---------------- APPLE ----------------
  const handleApple = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (busy || appleLock.current) return;
    appleLock.current = true;

    setError(null);
    setBusy(true);
    try {
      await signInWithApple();
      const fbUser = auth.currentUser;
      if (!fbUser) throw new Error("Apple login gelukt maar geen user gevonden.");
      const idToken = await fbUser.getIdToken(true);
      await userService.oauthLogin({ idToken, provider: "apple" });

      await refreshAuth();
      closeAll();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? "";
      if (code === "auth/popup-closed-by-user") return;
      if (code === "auth/cancelled-popup-request") return;
      setError(getErrorMessage(err, "Apple login mislukt"));
    } finally {
      setBusy(false);
      appleLock.current = false;
    }
  };

  // ---------------- SIGNUP helpers ----------------
  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onPickAvatar = () => fileRef.current?.click();

  const onAvatarSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAvatarUrl(url);
  };

  const pwInvalid = (form.wachtwoord || "").length < 8;
  const showPwError = form.wachtwoord.length > 0 && pwInvalid;

  const submitSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (pwInvalid) {
      setError("Wachtwoord moet minstens 8 karakters hebben.");
      return;
    }

    setBusy(true);
    try {
      const fallbackUsername =
        `${form.voornaam} ${form.naam}`.trim() || form.email.split("@")[0] || form.email;
      const preferredLanguage =
        (typeof navigator !== "undefined" && navigator.language) || "nl";
      const created = await userService.signUp({
        email: form.email,
        password: form.wachtwoord,
        username: fallbackUsername,
        firstname: form.voornaam,
        lastname: form.naam,
        street: form.straat,
        number: form.huisnummer,
        addition: form.toevoeging || undefined,
        zip: form.postcode,
        city: form.stad,
        country: form.land,
        picture: avatarUrl ?? "",
        bio: "",
        geo_hide: true,
        language: preferredLanguage,
        iban: form.iban,
        bic: form.bic,
      });

      trackCompleteRegistration("email", created?.id ?? "");

      await login(form.email, form.wachtwoord);
      closeAll();
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Account aanmaken mislukt"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-backdrop" onClick={closeAll}>
      <div onClick={(e) => e.stopPropagation()}>
        {view === "landing" && (
          <AuthLandingView
            busy={busy}
            error={error}
            onGoogleSuccess={handleGoogleSuccess}
            onApple={handleApple}
            onLogin={openLogin}
            onSignup={openSignupEmail}
            onClose={closeAll}
          />
        )}

        {view === "login" && (
          <LoginView
            username={username}
            password={password}
            busy={busy}
            error={error}
            firstInputRef={firstInputRef}
            onUsernameChange={setUsername}
            onPasswordChange={setPassword}
            onSubmit={submitLogin}
            onGoogleSuccess={handleGoogleSuccess}
            onApple={handleApple}
            onBack={() => setView("landing")}
            onClose={closeAll}
          />
        )}

        {view === "email" && (
          <SignupView
            form={form}
            avatarUrl={avatarUrl}
            busy={busy}
            error={error}
            showPwError={showPwError}
            pwInvalid={pwInvalid}
            firstInputRef={firstInputRef}
            fileRef={fileRef}
            onChange={onChange}
            onPickAvatar={onPickAvatar}
            onAvatarSelected={onAvatarSelected}
            onSubmit={submitSignup}
            onBack={() => setView("landing")}
          />
        )}
      </div>
    </div>
  );
}
